import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { CompanyCustomTheme, CompanyFeatures, CompanyWebTheme } from 'src/lib/fliko/models/company/company.data';
import { CompanyLog } from 'src/lib/fliko/models/company/companyLog.data';
import { CompanyEmployeeData, EmployeeData, EmployeeHistoryRecord } from 'src/lib/fliko/models/company/employee.data';
import { SsoDomainData, SsoUserData, SsoWhitelistData } from 'src/lib/fliko/models/company/sso.data';
import { EstateDbSettingsData } from 'src/lib/fliko/models/estate/estate.data';
import { UpdateEmployee3cxNumberRequest } from 'src/lib/fliko/models/http/threeCx.data';
import { Nullish } from 'src/lib/fliko/types/typeish.type';
import { DatabaseService } from 'src/app/shared/services/database.service';
import { FunctionsApiService } from 'src/app/shared/services/functions-api.service';
import { EstateService } from 'src/app/view/estate-view/services/estate.service';

/**
 * Handler for Company Global Data Service functions
 */
@Injectable({ providedIn: 'root' })
export class CompanyService {
  private employeesByCompany = new Map<string, Promise<EmployeeData[]>>();

  constructor(
    private dbSrv: DatabaseService,
    private fas: FunctionsApiService,
    private estateService: EstateService
  ) {}

  // #region Employee

  /**
   * @path /companyEmployee/$id
   * @key userId
   * @database shard
   */
  private companyEmployee = this.dbSrv.companyFactory.getCollection<CompanyEmployeeData>('companyEmployee/$cId');

  public getCompanyEmployees$(): Observable<CompanyEmployeeData[]> {
    return this.companyEmployee.getList();
  }

  private companyEmpById = (id: string) => this.dbSrv.companyFactory.getObject<CompanyEmployeeData>(`companyEmployee/$cId/${id}`);

  public getCompanyEmployee$(id: string): Observable<CompanyEmployeeData | Nullish> {
    return this.companyEmpById(id).get();
  }

  /**
   * @path /employee/$id
   * @key userId
   * @database common
   */
  private employeeById = (id: string) => this.dbSrv.commonFactory.getObject<EmployeeData>(`employee/${id}`);

  public getEmployee$(employeeId: string): Observable<EmployeeData | Nullish> {
    return this.employeeById(employeeId).get();
  }

  public addEmployeeLogin(employeeId: string, nextIndex: number, companyId?: string): Promise<void> {
    this.invalidateEmployeesCache(companyId);
    if (nextIndex == 0) return this.employeeById(employeeId).update({ lastLogins: [Date.now()] });
    // TODO: transact
    return this.employeeById(employeeId).setChild(`lastLogins/${nextIndex}` as any, Date.now());
  }

  public updateEmployee3cxNumber(employeeId: string, number: string | null, companyId: string): Promise<void> {
    this.invalidateEmployeesCache(companyId);
    return this.fas
      .callFunctionOrThrow<UpdateEmployee3cxNumberRequest, boolean>('updateEmployee3cxNumber', {
        companyId,
        userId: employeeId,
        number
      })
      .then(() => undefined);
  }

  public getEmployees(companyId: string): Promise<EmployeeData[]> {
    const cached = this.employeesByCompany.get(companyId);
    if (cached) return cached;

    const request = this.fas
      .callFunction<EmployeeData[]>('companyEmployeesCommon', { companyId })
      .then((employees) => (Array.isArray(employees) ? employees : []))
      .catch((error) => {
        this.employeesByCompany.delete(companyId);
        throw error;
      });
    this.employeesByCompany.set(companyId, request);

    return request;
  }

  private invalidateEmployeesCache(companyId?: string): void {
    if (companyId) {
      this.employeesByCompany.delete(companyId);
      return;
    }

    this.employeesByCompany.clear();
  }

  /**
   * @path /employeeHistoryRecord/
   * @key id
   * @database shard
   */
  private employeeHistoryRecord = (employeeId: string) =>
    this.dbSrv.companyFactory.getCollection<EmployeeHistoryRecord>(`employeeHistoryRecord/$cId/${employeeId}`);

  public getEmployeeHistoryRecord$(userId: string): Observable<EmployeeHistoryRecord[]> {
    return this.employeeHistoryRecord(userId).getList();
  }
  // #endregion Employee

  // #region Custom Theme
  /**
   * @path /companyCustomTheme/$companyId
   * @database common
   */
  private companyCustomTheme = (companyId: string) =>
    this.dbSrv.commonFactory.getObject<CompanyCustomTheme>(`companyCustomTheme/${companyId}`);

  public getTheme$(companyId: string): Observable<CompanyCustomTheme | Nullish> {
    return this.companyCustomTheme(companyId).get();
  }

  public saveTheme(companyId: string, value: CompanyWebTheme): Promise<void> {
    return this.companyCustomTheme(companyId).update({ web: value });
  }
  // #endregion Custom Theme

  /**
   * @path /companyLog/$companyId
   * @key id
   * @database shard
   */
  private get companyLog() {
    return this.dbSrv.companyFactory.getCollection<CompanyLog>('companyLog/$cId');
  }

  public getEventLog(): Observable<CompanyLog[]> {
    return this.companyLog.getList();
  }

  /**
   * @path /companyFeature/$companyId
   * @database shard
   */
  private get companyFeatures() {
    return this.dbSrv.companyFactory.getObject<CompanyFeatures>('companyFeature/$cId');
  }

  public getFeatures$(): Observable<CompanyFeatures | Nullish> {
    return this.companyFeatures.get();
  }

  // #region Sso

  private ssoDomains = (companyId: string) =>
    this.dbSrv.commonFactory.getCollectionReadOnly<SsoDomainData>('ssoDomains/', {
      by: { child: 'companyId' },
      equalTo: companyId
    });

  public getSsoDomains$(companyId: string): Observable<SsoDomainData[]> {
    return this.ssoDomains(companyId).getList();
  }

  private ssoWhitelist = (companyId: string) =>
    this.dbSrv.commonFactory.getCollection<SsoWhitelistData>(`ssoRegistry/${companyId}/whitelist`);

  public getSsoWhitelist$(companyId: string): Observable<SsoWhitelistData[]> {
    return this.ssoWhitelist(companyId).getList();
  }

  private ssoUsers = (companyId: string) => this.dbSrv.commonFactory.getCollection<SsoUserData>(`ssoRegistry/${companyId}/users`);

  public getSsoUsers$(companyId: string): Observable<SsoUserData[]> {
    return this.ssoUsers(companyId).getList();
  }

  // #endregion Sso

  /**
   * @param {string} estateId
   * @path /estateDbSettings/$estateId
   */
  public getEstateDbSettings$(estateId: string): Observable<EstateDbSettingsData | Nullish> {
    return this.estateService.getEstateDbSettings$(estateId);
  }
}
